import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Save, Lock, AlertCircle, Clock, XCircle } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiErrorField } from "../../lib/api";
import { validate, splitList, errorText } from "../../lib/validate";
import { Field, inputProps, OptionList, VCOLOR, VERTICAL_KEYS, ExampleBadge } from "../site/Primitives";
import { PhoneOptinFields, phoneRules, toPayloadPhone } from "./PhoneOptin";
import { VerifiedBadge, expertVerifiedSince } from "../site/VerifiedBadge";

const EMPTY = { name: "", country: "", verticals: [], skills: "", hourly_rate: "", availability: "immediate", phone: "", whatsapp_optin: false };
const RULES = { name: ["required", ["min", 2]], country: ["required", ["min", 2]], verticals: [["list", "pickVertical"]], skills: ["required"], hourly_rate: ["required", "rate"] };

const VerticalChips = ({ value, onChange, error }) => {
  const { t } = useLang();
  const toggle = (k) => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  const msg = errorText(t, error);
  return (
    <div className="f-field f-span2" role="group" aria-labelledby="expert-verticals-label">
      <span className="f-label" id="expert-verticals-label">{t.pro.expert.verticals}<span style={{ color: "var(--amber)", marginLeft: 3 }} aria-hidden="true">*</span></span>
      <div className="chips" id="expert-verticals">
        {VERTICAL_KEYS.map((k) => (
          <button key={k} type="button" className="chip" aria-pressed={value.includes(k)} style={{ "--c": VCOLOR[k] }} onClick={() => toggle(k)} data-testid={`expert-vertical-chip-${k}`}>
            <i aria-hidden="true" />{t.verticals[k]}
          </button>
        ))}
      </div>
      {msg && <span className="f-error" role="alert" data-testid="expert-verticals-error"><AlertCircle aria-hidden="true" /> {msg}</span>}
    </div>
  );
};

const STATUS_UI = { pending: [Clock, "var(--amber)"], approved: [CheckCircle2, "#6FCF97"], rejected: [XCircle, "var(--rust-text)"] };

const StatusNote = ({ profile }) => {
  const { t } = useLang();
  const a = t.approval;
  const [Icon, c] = STATUS_UI[profile.status] || STATUS_UI.pending;
  return (
    <p className="status-note" role="status" style={{ "--c": c }} data-testid="expert-status-note" data-status={profile.status}>
      <Icon aria-hidden="true" />
      <span><b>{a.status[profile.status]}</b> — {a.desc[profile.status]}
        {profile.status === "rejected" && profile.rejection_reason && <><br /><b>{a.reason}</b> {profile.rejection_reason}</>}
      </span>
    </p>
  );
};

export const ExpertProfile = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const x = t.pro.expert;
  const d = t.dossier;
  const [values, setValues] = useState(EMPTY);
  const [profile, setProfile] = useState(undefined);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ sending: false, saved: false, failed: false });

  useEffect(() => {
    api.get("/me/expert").then(({ data }) => {
      setProfile(data);
      if (data) setValues({ ...EMPTY, ...data, skills: data.skills.join(", "), hourly_rate: String(data.hourly_rate), phone: data.phone || "" });
      else setValues((v) => ({ ...v, name: user.name || "" }));
    });
  }, [user]);

  const setVal = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setErrors((er) => ({ ...er, [k]: undefined })); };
  const f = (k, req = true) => ({ ...inputProps(`expert-${k}`, errors[k], req), value: values[k], onChange: (e) => setVal(k, e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { ...RULES, ...phoneRules(values) });
    if (!errs.skills && splitList(values.skills).length === 0) errs.skills = ["skills"];
    setErrors(errs);
    if (Object.keys(errs).length) return document.getElementById(`expert-${Object.keys(errs)[0]}`)?.focus?.();
    setStatus({ sending: true, saved: false, failed: false });
    try {
      const payload = { name: values.name, country: values.country, verticals: values.verticals, availability: values.availability,
        skills: splitList(values.skills), hourly_rate: Number(values.hourly_rate), phone: toPayloadPhone(values.phone), whatsapp_optin: values.whatsapp_optin, lang };
      const { data } = await api.put("/me/expert", payload);
      setProfile(data);
      setStatus({ sending: false, saved: true, failed: false });
    } catch (err) {
      setErrors(apiErrorField(err));
      setStatus({ sending: false, saved: false, failed: true });
    }
  };

  if (profile === undefined) return <div className="panel pro-panel"><Loader2 className="spin" aria-label={t.common.loading} /></div>;
  return (
    <div className="panel pro-panel" data-testid="expert-profile-panel">
      <div className="sub-head" style={{ marginBottom: 8 }}>
        <h3 className="sub-title">{d.expertTitle} — {d.profile} {profile?.is_example && <ExampleBadge />}</h3>
        <VerifiedBadge since={expertVerifiedSince(profile)} testid="expert-verified-badge" />
      </div>
      {profile && <StatusNote profile={profile} />}
      <p className="pro-intro">{profile ? x.intro : d.noProfileExpert}</p>
      <form onSubmit={submit} noValidate data-testid="expert-form">
        <div className="f-grid">
          <Field id="expert-name" label={x.name} error={errors.name} required><input type="text" autoComplete="name" {...f("name")} /></Field>
          <div className="f-field"><span className="f-label">{d.emailLocked}</span><div className="rate-display" style={{ color: "var(--text-1)" }} data-testid="expert-email-display">{user.email}</div></div>
          <Field id="expert-country" label={x.country} error={errors.country} required><input type="text" autoComplete="country-name" {...f("country")} /></Field>
          <Field id="expert-availability" label={x.availability}><select {...f("availability", false)}><OptionList map={t.availability} /></select></Field>
          <VerticalChips value={values.verticals} onChange={(v) => setVal("verticals", v)} error={errors.verticals} />
          <Field id="expert-skills" label={x.skills} error={errors.skills} required span2><input type="text" placeholder={x.skillsPh} {...f("skills")} /></Field>
          <Field id="expert-hourly_rate" label={x.rate} error={errors.hourly_rate} required><input type="number" min="20" max="1000" step="5" placeholder="85" inputMode="decimal" {...f("hourly_rate")} /></Field>
          <div className="f-field" />
          <PhoneOptinFields prefix="expert" values={values} errors={errors} setVal={setVal} />
        </div>
        <p className="info-note"><Lock aria-hidden="true" /><span>{x.payNote}</span></p>
        <div className="f-actions">
          <span className="f-note">{x.note}</span>
          <button type="submit" className="btn btn-primary" disabled={status.sending} data-testid="expert-submit-btn">
            {status.sending ? <Loader2 className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}{d.saveProfile}
          </button>
        </div>
      </form>
      {status.saved && <p className="success-msg" role="status" data-testid="expert-success"><CheckCircle2 aria-hidden="true" />{d.saved}</p>}
      {status.failed && <p className="error-banner" role="alert"><AlertCircle aria-hidden="true" />{Object.keys(errors).length ? t.errors.fixErrors : t.errors.generic}</p>}
    </div>
  );
};
